import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import fetchWithAuth from "@/api/secureFetch";
import { getIntelligenicToken } from "./getIntelligenicToken";

export interface AccessManifest {
    contract_version: number;
    user_id: number;
    tenant_id: number | null;
    subscription: {
        plan_code: string;
        status: string;
        current_period_end: string | null;
        cancel_at_period_end: boolean;
    } | null;
    entitlements: {
        context_mesh: boolean;
        product_studio: boolean;
        account_billing: boolean;
    };
    capabilities: string[];
    default_experience: "context_mesh_console" | "product_hub" | "account_billing";
    policy_epoch: number;
    resolution_state: "resolved" | "pending" | "failed";
}

interface AccessManifestContextValue {
    manifest: AccessManifest | null;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<AccessManifest | null>;
    hasCapability: (capability: string) => boolean;
}

const AccessManifestContext = createContext<AccessManifestContextValue>({
    manifest: null,
    loading: false,
    error: null,
    refresh: async () => null,
    hasCapability: () => false,
});

export function AccessManifestProvider({ children }: { children: React.ReactNode }) {
    const { getToken, isSignedIn, isLoaded, userId } = useAuth();
    const [manifest, setManifest] = useState<AccessManifest | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const requestInFlight = useRef<Promise<AccessManifest | null> | null>(null);
    const manifestRef = useRef<AccessManifest | null>(null);
    const manifestSubjectRef = useRef<string | null>(null);
    const requestVersionRef = useRef(0);

    const refresh = useCallback(async () => {
        if (!isSignedIn || !userId) {
            manifestRef.current = null;
            manifestSubjectRef.current = null;
            setManifest(null);
            setLoading(false);
            return null;
        }
        if (requestInFlight.current) return requestInFlight.current;
        const requestVersion = requestVersionRef.current;
        // Focus/visibility polling is a background refresh. Do not replace an
        // already-rendered application with the global loading screen.
        if (manifestRef.current === null) setLoading(true);
        setError(null);
        const request = (async () => {
            try {
                const token = await getIntelligenicToken(getToken);
                if (!token) throw new Error("Missing authentication token");
                const response = await fetchWithAuth("/api/access-manifest", token, { method: "GET" });
                if (!response.ok) throw new Error(`Access manifest failed (${response.status})`);
                const next = await response.json() as AccessManifest;
                if (requestVersion !== requestVersionRef.current) return null;
                manifestRef.current = next;
                manifestSubjectRef.current = userId;
                setManifest(next);
                return next;
            } catch (cause) {
                if (requestVersion !== requestVersionRef.current) return null;
                // Fail closed: AuthGuard replaces protected content with a retryable state.
                manifestRef.current = null;
                manifestSubjectRef.current = null;
                setManifest(null);
                setError(cause instanceof Error ? cause.message : "Access manifest failed");
                return null;
            } finally {
                if (requestVersion === requestVersionRef.current) {
                    requestInFlight.current = null;
                    setLoading(false);
                }
            }
        })();
        requestInFlight.current = request;
        return request;
    }, [getToken, isSignedIn, userId]);

    useEffect(() => {
        requestVersionRef.current += 1;
        requestInFlight.current = null;
        manifestRef.current = null;
        manifestSubjectRef.current = null;
        setManifest(null);
        setError(null);
        if (!isLoaded) {
            setLoading(true);
            return;
        }
        if (!isSignedIn || !userId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        void refresh();
    }, [isLoaded, isSignedIn, userId, refresh]);

    // Entitlements can change while the SPA remains open (upgrade, downgrade,
    // cancellation or an administrator change). Re-read the manifest on focus,
    // visibility changes, and periodically so policy_epoch changes take effect
    // without requiring a new login.
    useEffect(() => {
        if (!isLoaded || !isSignedIn) return;
        const refreshWhenVisible = () => {
            if (document.visibilityState === "visible") void refresh();
        };
        const intervalId = window.setInterval(refreshWhenVisible, 60_000);
        window.addEventListener("focus", refreshWhenVisible);
        document.addEventListener("visibilitychange", refreshWhenVisible);
        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener("focus", refreshWhenVisible);
            document.removeEventListener("visibilitychange", refreshWhenVisible);
        };
    }, [isLoaded, isSignedIn, refresh]);

    const effectiveManifest = manifestSubjectRef.current === userId ? manifest : null;
    const identityChanging = Boolean(isSignedIn && userId && manifest && !effectiveManifest);
    const value = useMemo<AccessManifestContextValue>(() => ({
        manifest: effectiveManifest,
        loading: loading || identityChanging,
        error,
        refresh,
        hasCapability: capability => effectiveManifest?.capabilities.includes(capability) ?? false
    }), [effectiveManifest, loading, identityChanging, error, refresh]);

    return <AccessManifestContext.Provider value={value}>{children}</AccessManifestContext.Provider>;
}

export function useAccessManifest(): AccessManifestContextValue {
    return useContext(AccessManifestContext);
}
